import { getEnvByHostname } from '../utils/env';
import React, { useState } from 'react';
import { Card, Form, Input, Button, Space, message, Descriptions, Tag, Empty, Row, Col, Statistic, Timeline, QRCode, Alert, Typography } from 'antd';
import { SearchOutlined, ReloadOutlined, ShoppingOutlined, DollarOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, SyncOutlined, LinkOutlined, QrcodeOutlined, UserOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageCache } from '../contexts/PageCacheContext';
import { testApi } from '../services/api';
import dayjs from 'dayjs';

const fmt = (ts) => ts ? dayjs(Number(ts)).format('YYYY-MM-DD HH:mm:ss') : '-';

// QR 码 Version 40 在 L 级纠错下的二进制上限约 2953 字节，留出安全余量
const QR_MAX_LEN = 2900;

// 兜底：捕获 QRCode 内部抛出的异常，避免整页白屏
class QRErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value && this.state.hasError) this.setState({ hasError: false });
  }
  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

// 安全渲染二维码：内容超长时降级为可复制文本
function SafeQRCode({ value }) {
  const content = value || '';
  const fallback = (
    <div style={{ maxWidth: 480 }}>
      <Alert type="warning" showIcon style={{ marginBottom: 12 }}
        message="内容过长，无法生成二维码" description="请复制以下支付内容，使用支付 App 手动粘贴付款。" />
      <Typography.Paragraph copyable={{ text: content }}
        style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 12px', wordBreak: 'break-all', margin: 0, fontFamily: 'Monaco, Consolas, monospace', fontSize: 13 }}>
        {content}
      </Typography.Paragraph>
    </div>
  );
  if (content.length > QR_MAX_LEN) return fallback;
  return (
    <QRErrorBoundary fallback={fallback} value={content}>
      <QRCode value={content} size={200} errorLevel="L" />
    </QRErrorBoundary>
  );
}

export default function OrderQuery() {
  const [form] = Form.useForm();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const { cache, save: saveCache } = usePageCache('orderQuery');

  React.useEffect(() => {
    if (cache) {
      if (cache.orderData) setOrderData(cache.orderData);
      if (cache.formValues) form.setFieldsValue(cache.formValues);
    }
  }, []);
  React.useEffect(() => {
    return () => { saveCache({ orderData, formValues: form.getFieldsValue() }); };
  });

  const handleQuery = async (values) => {
    setLoading(true);
    try {
      const requestBody = { orderId: values.orderId, requestId: values.requestId || `req_${Date.now()}` };
      const { data } = await testApi.executeTest({
        businessPartner: 'DEMO_ACCOUNT', env: getEnvByHostname(),
        testType: 'PAYIN_QUERY', requestBody: JSON.stringify(requestBody),
        executor: localStorage.getItem('username')
      });
      const res = data.data?.responseBody ? JSON.parse(data.data.responseBody) : data;
      if (res.code === '200' || res.code === 200) { setOrderData(res); }
      else { message.error(`${t('queryFailed')}: ${res.msg || t('queryFailed')}`); setOrderData(null); }
    } catch (error) {
      message.error(error.response?.data?.msg || t('queryFailed'));
    } finally { setLoading(false); }
  };

  const getStatusTag = (status) => {
    const cfg = { SUCCESS: { color: '#52c41a', icon: <CheckCircleOutlined /> }, PROCESSING: { color: '#1890ff', icon: <SyncOutlined spin /> }, FAILED: { color: '#ff4d4f', icon: <CloseCircleOutlined /> }, PENDING: { color: '#faad14', icon: <ClockCircleOutlined /> } };
    const c = cfg[status] || cfg.PENDING;
    return <Tag color={c.color} icon={c.icon} style={{ fontSize: 14, padding: '4px 12px' }}>{status}</Tag>;
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div className="fade-in-up">
        <Card className="modern-card"
          title={<Space size={16}><SearchOutlined style={{ color: '#667eea', fontSize: 20 }} /><span style={{ fontSize: 20, fontWeight: 600 }}>{t('payinQuery')}</span></Space>}
          extra={<Button icon={<ReloadOutlined />} onClick={() => { form.resetFields(); setOrderData(null); }} type="text">{t('reset')}</Button>}
          style={{ marginBottom: 24 }}>
          <Form form={form} layout="vertical" onFinish={handleQuery} size="large">
            <Row gutter={24}>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 500 }}>{t('payinOrderId')}</span>} name="orderId"
                  rules={[{ required: true, message: t('pleaseInput', t('payinOrderId')) }]}>
                  <Input placeholder="e.g. DP2025121511060001" prefix={<ShoppingOutlined style={{ color: '#9ca3af' }} />} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 500 }}>Request ID ({t('optional')})</span>} name="requestId">
                  <Input placeholder={t('autoGenerate')} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading} className="primary-button" style={{ height: 48 }}>{t('search')}</Button>
            </Form.Item>
          </Form>
        </Card>

        {orderData?.data ? (
          <div className="fade-in-up">
            <Row gutter={16} style={{ marginBottom: 24 }}>
              {[
                { title: t('payinStatus'), value: orderData.data.status, icon: <SyncOutlined spin={orderData.data.status === 'PROCESSING'} style={{ color: '#667eea' }} />, fontSize: 18 },
                { title: t('amount'), value: orderData.data.amount, icon: <DollarOutlined style={{ color: '#667eea' }} />, fontSize: 24, suffix: orderData.data.currency },
                { title: t('fee'), value: orderData.data.fee, fontSize: 24, suffix: orderData.data.currency },
                { title: t('depositMethod'), value: orderData.data.depositMethod, fontSize: 13 },
              ].map((s, i) => (
                <Col xs={24} sm={12} md={6} key={i}>
                  <Card className="modern-card" style={{ background: '#fff', borderLeft: '4px solid #667eea' }}>
                    <Statistic title={<span style={{ color: '#6b7280', fontSize: 14 }}>{s.title}</span>} value={s.value}
                      valueStyle={{ color: '#1f2937', fontSize: s.fontSize, fontWeight: 600 }} prefix={s.icon} suffix={s.suffix} />
                  </Card>
                </Col>
              ))}
            </Row>

            <Card className="modern-card"
              title={<Space><ShoppingOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('payinOrderDetail')}</span>{getStatusTag(orderData.data.status)}</Space>}
              style={{ marginBottom: 24 }}>
              <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="middle">
                <Descriptions.Item label={t('channelPayinOrderId')} span={2}><code style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, fontSize: 14 }}>{orderData.data.orderId}</code></Descriptions.Item>
                <Descriptions.Item label={t('status')}>{getStatusTag(orderData.data.status)}</Descriptions.Item>
                <Descriptions.Item label={t('payinOrderId')} span={2}><code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{orderData.data.externalOrderId}</code></Descriptions.Item>
                <Descriptions.Item label={t('subStatus')}><Tag color="blue">{orderData.data.subStatus}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('channelCode')}><Tag color="purple">{orderData.data.channelCode}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('accountId')}><code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{orderData.data.accountId}</code></Descriptions.Item>
                <Descriptions.Item label={t('depositMethod')}><Tag color="green">{orderData.data.depositMethod}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('currency')}><Tag color="orange">{orderData.data.currency}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('amount')}><span style={{ fontSize: 16, fontWeight: 600, color: '#52c41a' }}>{orderData.data.amount} {orderData.data.currency}</span></Descriptions.Item>
                <Descriptions.Item label={t('fee')}><span style={{ fontSize: 16, fontWeight: 600, color: '#ff4d4f' }}>{orderData.data.fee} {orderData.data.currency}</span></Descriptions.Item>
                {orderData.data.errorCode && <Descriptions.Item label={t('errorCode')}><Tag color="error">{orderData.data.errorCode}</Tag></Descriptions.Item>}
                {orderData.data.errorMsg && <Descriptions.Item label={t('errorDetail')} span={orderData.data.errorCode ? 2 : 3}><pre style={{ margin: 0, padding: '8px 12px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, fontSize: 13, color: '#ff4d4f', fontFamily: 'Monaco, Consolas, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto' }}>{(() => { try { return JSON.stringify(JSON.parse(orderData.data.errorMsg), null, 2); } catch (e) { return orderData.data.errorMsg; } })()}</pre></Descriptions.Item>}
                <Descriptions.Item label={t('createTime')}>{fmt(orderData.data.createTime)}</Descriptions.Item>
                <Descriptions.Item label={t('completedTime')}>{fmt(orderData.data.completedTime)}</Descriptions.Item>
              </Descriptions>
            </Card>

            {/* 支付信息 - 处理中状态展示 */}
            {orderData.data.status === 'PROCESSING' && orderData.data.paymentInfo && (orderData.data.paymentInfo.payUrl || orderData.data.paymentInfo.barCode) && (
              <Card className="modern-card"
                title={<Space><LinkOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('paymentInfo')}</span></Space>}
                style={{ marginBottom: 24 }}>
                {orderData.data.paymentInfo.payType === 'URL' ? (
                  <div style={{ padding: '16px 0' }}>
                    <Button type="primary" icon={<LinkOutlined />}
                      href={orderData.data.paymentInfo.payUrl} target="_blank" rel="noopener noreferrer">
                      {t('openPaymentPage')}
                    </Button>
                  </div>
                ) : (
                  <div style={{ padding: '16px 0' }}>
                    <div style={{ marginBottom: 12, fontSize: 14, color: '#6b7280' }}>{t('scanToPay')}</div>
                    <SafeQRCode value={orderData.data.paymentInfo.barCode || ''} />
                  </div>
                )}
              </Card>
            )}

            {/* 付款人信息 - 成功状态展示 */}
            {orderData.data.status === 'SUCCESS' && orderData.data.senderInfo && Object.keys(orderData.data.senderInfo).length > 0 && (
              <Card className="modern-card"
                title={<Space><UserOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('senderInfo')}</span></Space>}
                style={{ marginBottom: 24 }}>
                <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="middle">
                  {orderData.data.senderInfo.iban != null && orderData.data.senderInfo.iban !== '' && (
                    <Descriptions.Item label={t('senderIban')}><code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{orderData.data.senderInfo.iban}</code></Descriptions.Item>
                  )}
                  {orderData.data.senderInfo.senderAcctName != null && orderData.data.senderInfo.senderAcctName !== '' && (
                    <Descriptions.Item label={t('senderAcctName')}>{orderData.data.senderInfo.senderAcctName}</Descriptions.Item>
                  )}
                  {orderData.data.senderInfo.bic != null && orderData.data.senderInfo.bic !== '' && (
                    <Descriptions.Item label={t('senderBic')}><code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{orderData.data.senderInfo.bic}</code></Descriptions.Item>
                  )}
                  {Object.entries(orderData.data.senderInfo)
                    .filter(([key, value]) => !['iban', 'senderAcctName', 'bic'].includes(key) && value != null && value !== '')
                    .map(([key, value]) => (
                      <Descriptions.Item key={key} label={key}>
                        {typeof value === 'object' ? <pre style={{ margin: 0, fontSize: 13 }}>{JSON.stringify(value, null, 2)}</pre> : String(value)}
                      </Descriptions.Item>
                    ))}
                </Descriptions>
              </Card>
            )}

            <Card className="modern-card" title={<Space><DollarOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('callbackConfig')}</span></Space>} style={{ marginBottom: 24 }}>
              <Row gutter={[24, 24]}>
                {[['callbackUrl', t('callbackUrl')], ['successRedirectUrl', t('successRedirect')], ['failRedirectUrl', t('failRedirect')]].map(([key, label]) => (
                  <Col xs={24} md={8} key={key}>
                    <Descriptions column={1} size="middle">
                      <Descriptions.Item label={<span style={{ fontWeight: 500 }}>{label}</span>}>
                        <a href={orderData.data[key]} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{orderData.data[key]}</a>
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                ))}
              </Row>
            </Card>

            <Card className="modern-card" title={<Space><ClockCircleOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('payinOrderTimeline')}</span></Space>}>
              <Timeline items={[
                orderData.data.completedTime && { color: 'green', children: <div><div style={{ fontWeight: 600, marginBottom: 4 }}>{t('payinCompleted')}</div><div style={{ color: '#6b7280', fontSize: 14 }}>{fmt(orderData.data.completedTime)}</div></div> },
                { color: orderData.data.status === 'PROCESSING' ? 'blue' : 'gray', children: <div><div style={{ fontWeight: 600, marginBottom: 4 }}>{t('payinProcessing')}</div><div style={{ color: '#6b7280', fontSize: 14 }}>{t('status')}: {orderData.data.subStatus || orderData.data.status}</div></div> },
                { color: 'blue', children: <div><div style={{ fontWeight: 600, marginBottom: 4 }}>{t('orderCreated')}</div><div style={{ color: '#6b7280', fontSize: 14 }}>{fmt(orderData.data.createTime)}</div></div> },
              ].filter(Boolean)} />
            </Card>
          </div>
        ) : (
          <Card className="modern-card">
            <Empty description={<span style={{ color: '#6b7280', fontSize: 16 }}>{t('enterPayinOrderId')}</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </Card>
        )}
      </div>
    </div>
  );
}
